import { pamtiPolaziste } from './polaziste'

/**
 * SAT RESORTA — jedini modul koji zna u kojoj se zoni čita vrijeme (D14-B §6).
 *
 * **Nalaz vlasnika, 14.08.2026.**: gost pošalje upit za 18:30, a ekran "Upit
 * poslan" mu vrati 16:30. Nitko nije pogriješio u računu — pisalo se u JEDNOJ
 * zoni (preglednikovoj), a čitalo u DRUGOJ (UTC-u), pa je razlika bila točno
 * ljetni pomak. Dva sata su ondje gdje su i sve druge razlike te vrste: na
 * granici na kojoj se trenutak pretvara u sat.
 *
 * **Zato jedan modul, a ne pravilo.** Raspored resorta je zidni sat: "subotom u
 * 18:30" znači 18:30 u Zagrebu, a ne 18:30 negdje. Trenutak koji putuje žicom je
 * UTC i to ostaje. Pretvorba između to dvoje smije postojati SAMO ovdje — svako
 * drugo mjesto koje je izvede razilazi se prvom promjenom (isto pravilo kao
 * `QuoteEngine` i `lib/matrica.ts`, CLAUDE.md §16a).
 *
 * **Zona je konstanta dok `tenant.timezone` ne postoji** (CLAUDE.md §18) — ista
 * ona koju poslužitelj drži u `BoravakVremena.ZadanaZona`, s istim imenom, da
 * zamjena jednog dana bude jedan parametar, a ne potraga. Zrcalo mjeri
 * `zona.test.ts`; test zonu FIKSIRA, jer bi inače mjerio preglednik, a ne račun.
 *
 * **Preglednikova zona se ne pita nigdje.** Gost u zrakoplovu ima mobitel na
 * krivom vremenu, a resort ima jedan sat — onaj na zidu recepcije.
 */

/** Zona resorta; zrcalo poslužiteljske `BoravakVremena.ZadanaZona`. */
export const ZONA_RESORTA = 'Europe/Zagreb'

/**
 * Rezervni pomak kad izvedba nema tzdata (isti izlaz kao `BoravakFaze.Zona()`:
 * fiksni CET). Pogrešan sat je bolji od srušenog ekrana — ali samo ondje gdje
 * zone stvarno nema, a to danas nije nijedan preglednik.
 */
const REZERVNI_POMAK_MIN = 60

interface Dijelovi {
  godina: number
  mjesec: number
  dan: number
  sat: number
  minuta: number
  sekunda: number
}

/**
 * Formater koji trenutak razlaže na ZIDNI SAT resorta. Gradi se jednom i lijeno:
 * `Intl` s imenovanom zonom je skup, a pita ga se na svakoj kartici.
 *
 * `null` znači "izvedba ovu zonu ne zna" — tada se pada na fiksni pomak.
 */
let formater: Intl.DateTimeFormat | null | undefined

// Lijeni formater je procesni predmemorij: test koji `Intl` podmetne ne smije ga
// ostaviti sljedećem (D15-A §1a).
pamtiPolaziste(() => {
  formater = undefined
})

function razlagac(): Intl.DateTimeFormat | null {
  if (formater !== undefined) return formater

  try {
    formater = new Intl.DateTimeFormat('en-US', {
      timeZone: ZONA_RESORTA,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    formater = null
  }

  return formater
}

/** Trenutak razložen na zidni sat resorta. */
function dijelovi(trenutak: Date): Dijelovi {
  const alat = razlagac()

  if (!alat) {
    const pomaknut = new Date(trenutak.getTime() + REZERVNI_POMAK_MIN * 60_000)

    return {
      godina: pomaknut.getUTCFullYear(),
      mjesec: pomaknut.getUTCMonth() + 1,
      dan: pomaknut.getUTCDate(),
      sat: pomaknut.getUTCHours(),
      minuta: pomaknut.getUTCMinutes(),
      sekunda: pomaknut.getUTCSeconds(),
    }
  }

  const polja: Record<string, number> = {}

  for (const dio of alat.formatToParts(trenutak)) {
    if (dio.type !== 'literal') polja[dio.type] = Number(dio.value)
  }

  return {
    godina: polja.year ?? 0,
    mjesec: polja.month ?? 1,
    dan: polja.day ?? 1,
    // `h23` daje 00–23; starije izvedbe znaju vratiti 24 za ponoć.
    sat: (polja.hour ?? 0) % 24,
    minuta: polja.minute ?? 0,
    sekunda: polja.second ?? 0,
  }
}

/** Koliko je zona resorta ispred UTC-a u TOM trenutku (ms; ljeti +2 h). */
function pomakMs(trenutak: Date): number {
  const d = dijelovi(trenutak)
  const kaoUtc = Date.UTC(d.godina, d.mjesec - 1, d.dan, d.sat, d.minuta, d.sekunda)

  // Milisekunde se odbacuju na obje strane, inače bi ostatak ušao u pomak.
  return kaoUtc - Math.floor(trenutak.getTime() / 1000) * 1000
}

function dvije(broj: number): string {
  return broj.toString().padStart(2, '0')
}

/** Je li niz upotrebljiv trenutak; prazan i neispravan ulaz nisu greška nego "nema". */
export function trenutakIli(iso: string | null | undefined): Date | null {
  if (!iso) return null

  const trenutak = new Date(iso)

  return Number.isNaN(trenutak.getTime()) ? null : trenutak
}

/** `YYYY-MM-DD` po satu resorta — dan kakav stoji u rasporedu. */
export function datumZone(trenutak: Date): string {
  const d = dijelovi(trenutak)

  return `${d.godina}-${dvije(d.mjesec)}-${dvije(d.dan)}`
}

/** `HH:mm` po satu resorta — sat kakav gost čita ("18:30"). */
export function satZone(trenutak: Date): string {
  const d = dijelovi(trenutak)

  return `${dvije(d.sat)}:${dvije(d.minuta)}`
}

/** Današnji dan po satu resorta, ne po satu preglednika. */
export function danasZone(sada: Date = new Date()): string {
  return datumZone(sada)
}

/**
 * ZIDNI SAT RESORTA → TRENUTAK. Obrnuti smjer od {@link satZone}, i jedini na
 * kojem se sat pretvara u nešto što putuje žicom.
 *
 * **Ljetno računanje vremena se ne zaobilazi** (isto pravilo i ista dva izbora
 * kao `BoravakVremena.UTrenutak` na poslužitelju):
 *
 * · sat koji postoji DVAPUT (02:30 pri povratku na zimsko) daje RANIJI od dva —
 *   onaj koji je gostu na satu kad se pravilo prvi put ispuni;
 * · sat koji NE POSTOJI (02:30 pri prelasku na ljetno) pomiče se za prijelaz, pa
 *   nastane najraniji trenutak koji tog dana stvarno postoji — umjesto da obrazac
 *   padne na "Invalid Date".
 *
 * Kandidati se traže s pomakom koji vrijedi POLA DANA prije i pola dana poslije:
 * jedan prijelaz je između njih, pa su ta dva pomaka sve što zona tog dana ima.
 */
const POLA_DANA_MS = 12 * 60 * 60 * 1000

export function trenutakZone(datum: string, vrijeme: string): Date | null {
  const [godina, mjesec, dan] = datum.split('-').map(Number)
  const [sat, minuta] = vrijeme.split(':').map(Number)

  if (![godina, mjesec, dan, sat, minuta].every((broj) => Number.isFinite(broj))) return null

  const zidni = Date.UTC(godina!, mjesec! - 1, dan!, sat!, minuta!)

  if (Number.isNaN(zidni)) return null

  const pomaci = [
    pomakMs(new Date(zidni - POLA_DANA_MS)),
    pomakMs(new Date(zidni + POLA_DANA_MS)),
  ]

  // Raniji trenutak prvi: dvoznačan sat time dobiva ljetnu (raniju) polovicu.
  const kandidati = [...new Set(pomaci.map((pomak) => zidni - pomak))].sort((a, b) => a - b)
  const pogodak = kandidati.find((ms) => ms + pomakMs(new Date(ms)) === zidni)

  if (pogodak !== undefined) return new Date(pogodak)

  // Sat koji u zoni ne postoji: pomakni ga za trajanje prijelaza (u Europi 1 h).
  const pomaknut = zidni + Math.abs(pomaci[1]! - pomaci[0]!)

  return new Date(pomaknut - pomakMs(new Date(pomaknut)))
}

/**
 * Dan pomaknut za N dana, po KALENDARU a ne po milisekundama.
 *
 * "Sutra" je sljedeći redak kalendara, a ne 86 400 000 ms poslije: na dan
 * pomicanja sata potonje daje isti dan (ili preskače jedan), i to točno u tjednu
 * u kojem se raspored najviše gleda.
 */
export function pomakniDan(datum: string, dana: number): string {
  const [godina, mjesec, dan] = datum.split('-').map(Number)

  if (![godina, mjesec, dan].every((broj) => Number.isFinite(broj))) return datum

  const pomaknut = new Date(Date.UTC(godina!, mjesec! - 1, dan! + dana))

  return `${pomaknut.getUTCFullYear()}-${dvije(pomaknut.getUTCMonth() + 1)}-${dvije(
    pomaknut.getUTCDate(),
  )}`
}

/**
 * Opcije `Intl`-a koje IMENA dana i mjeseca pišu po satu resorta. Sve što se
 * oblikuje iz trenutka mora ih nositi — bez njih preglednik zapadno od
 * Greenwicha ispiše prethodni dan.
 */
export const U_ZONI: Intl.DateTimeFormatOptions = { timeZone: ZONA_RESORTA }
